<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { MiniMap } from '@vue-flow/minimap'
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  MarkerType,
  useVueFlow,
  VueFlow,
} from '@vue-flow/core'
import { flows as flowApi } from '@/services/api'
import WorkflowNode from './workflows/WorkflowNode.vue'
import {
  WORKFLOW_BLOCKS,
  autoLayoutWorkflow,
  cloneNode,
  createBlankWorkflow,
  createNode,
  normalizeWorkflowRecord,
  serializeWorkflowDocument,
  simulateWorkflow,
  validateWorkflow,
} from './workflows/workflowEngine'
import {
  WORKFLOW_TEMPLATES,
  buildWorkflowFromTemplate,
  workflowCategories,
} from './workflows/workflowTemplates'

const route = useRoute()
const { fitView, screenToFlowCoordinate } = useVueFlow()

const nodeTypes = {
  workflowNode: WorkflowNode,
}

const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.Arrow },
  style: { stroke: '#94a3b8', strokeWidth: 2.5 },
}

const flows = ref([])
const nodes = ref([])
const edges = ref([])
const selectedNodeId = ref(null)
const selectedFlowId = ref(null)
const activeAgentId = ref(null)
const isLoading = ref(false)
const isSaving = ref(false)
const loadError = ref('')
const saveMessage = ref('')
const selectedNodeKind = ref('message')
const simulationInput = ref('I need an appointment next week')
const simulationOutput = ref(null)
const history = ref([])
const future = ref([])
const historyTimer = ref(null)

const workflowMeta = reactive({
  id: null,
  agent_id: null,
  name: 'Untitled workflow',
  description: '',
  status: 'draft',
  version: 1,
  validation_status: 'draft',
  settings: {
    snapToGrid: true,
    gridSize: 20,
    autoFit: true,
  },
})

const inspectorDraft = reactive({
  title: '',
  description: '',
  kind: 'message',
  text: '',
  greeting: '',
  language: 'english',
  voiceLabel: '',
  providerVoiceId: '',
  speakingStyle: 'clear',
  fieldsText: 'name, phone, service',
  requiredFieldsText: 'name, phone',
  prompt: '',
  branchesText: 'Yes: yes, book, ready\nNo: no, later, not now',
  baseName: '',
  fallbackMessage: '',
  service: '',
  duration: 30,
  calendar: '',
  confirmationChannel: 'sms',
  paymentMode: 'deposit',
  amount: 50,
  percentage: 20,
  currency: 'USD',
  paymentMessage: '',
  transferTarget: '',
  transferCondition: '',
  transferAnnounce: '',
  channel: 'sms',
  subject: '',
  template: '',
  method: 'POST',
  url: '',
  secret: '',
  closing: '',
  voiceTone: 'professional',
})

const selectedNode = computed(() => nodes.value.find((node) => node.id === selectedNodeId.value) || null)
const selectedFlow = computed(() => flows.value.find((flow) => String(flow.id) === String(selectedFlowId.value)) || null)
const validation = computed(() => validateWorkflow(composeWorkflowDocument()))
const validationIssues = computed(() => validation.value.issues)
const validationStatus = computed(() => validation.value.status)
const validationTone = computed(() => {
  if (validationStatus.value === 'ok') return 'ok'
  if (validationStatus.value === 'error') return 'error'
  return 'warning'
})
const workflowStatusLabel = computed(() => String(workflowMeta.status || 'draft').toUpperCase())
const workflowStatusTone = computed(() => {
  if (workflowMeta.status === 'active') return 'ok'
  if (workflowMeta.status === 'archived') return 'warning'
  return 'draft'
})
const isDraftWorkflow = computed(() => String(workflowMeta.status || 'draft') !== 'active')
const hasWorkflows = computed(() => flows.value.length > 0)
const showEmptyWorkflowPicker = ref(false)
const showEmptyWorkflowState = computed(() => showEmptyWorkflowPicker.value)
const availableBlocks = computed(() => WORKFLOW_BLOCKS.filter((block) => block.type !== 'voice'))
const workflowTemplates = computed(() => WORKFLOW_TEMPLATES)
const selectedTemplateCategory = ref('all')
const templateCategories = computed(() => workflowCategories)
const emptyWorkflowTemplate = {
  kind: 'empty',
  id: 'empty_workflow',
  key: 'empty_workflow',
  title: 'Empty workflow',
  name: 'Empty workflow',
  description: 'Start with a blank canvas and build your own workflow step by step.',
  icon: 'blank',
  tags: ['blank canvas', 'custom'],
}
const curatedTemplates = computed(() => workflowTemplates.value.filter((template) => template.featured))
const filteredTemplates = computed(() => {
  if (selectedTemplateCategory.value === 'all') {
    return [emptyWorkflowTemplate, ...curatedTemplates.value]
  }
  return workflowTemplates.value.filter((template) => (template.categoryId || template.category || 'general_business') === selectedTemplateCategory.value)
})
const hasSelection = computed(() => Boolean(selectedNode.value))
const selectedNodeLabel = computed(() => selectedNode.value?.data?.title || 'No block selected')

function readCookie(name) {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : ''
}

function resolveActiveAgentId() {
  const fromRoute = route.params.agentId || route.params.agent_id || route.query.agentId || route.query.agent_id
  const fromStorage = typeof window !== 'undefined'
    ? window.localStorage.getItem('active_agent_id') || window.localStorage.getItem('agent_id') || readCookie('active_agent_id')
    : ''
  const raw = fromRoute || fromStorage || ''
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function parseList(value) {
  return String(value || '')
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function parseBranches(value) {
  return parseList(value).map((line, index) => {
    const [labelPart, keywordPart] = line.split(':')
    const label = (keywordPart ? labelPart : `Branch ${index + 1}`).trim()
    const keywords = (keywordPart || labelPart)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
    return {
      id: label.toLowerCase().replace(/[^a-z0-9]+/g, '_') || `branch_${index + 1}`,
      label,
      keywords,
    }
  })
}

const TEMPLATE_ICON_SVGS = {
  blank: `
    <svg viewBox="0 0 24 24" fill="none">
      <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
      <path d="M4 9h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M12 12v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M9.5 14.5h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>`,
  calendar: `
    <svg viewBox="0 0 24 24" fill="none">
      <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
      <path d="M4 9h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M9 13l2 2 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`,
  'phone-return': `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M8.5 5.5c1.8 4 5 7.2 9 9l2-2a1.5 1.5 0 0 1 1.6-.3l1.9.8a1.5 1.5 0 0 1 .9 1.4V18c0 .9-.7 1.6-1.6 1.6-8.8 0-16-7.2-16-16C6.3 2.7 7 2 7.9 2h2.6c.6 0 1.2.4 1.4.9l.8 1.9a1.5 1.5 0 0 1-.3 1.6l-1.9 2Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
      <path d="M18 7v5h-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`,
  lead: `
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="8" r="3" stroke="currentColor" stroke-width="1.8" />
      <path d="M4.5 19c.7-3.1 2.9-4.8 4.5-4.8s3.8 1.7 4.5 4.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M14.5 8h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M17 5.5 19.5 8 17 10.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`,
  support: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M5 6.8A2.8 2.8 0 0 1 7.8 4h8.4A2.8 2.8 0 0 1 19 6.8v5.4A2.8 2.8 0 0 1 16.2 15H11l-4.8 3.5V15H7.8A2.8 2.8 0 0 1 5 12.2V6.8Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
      <path d="M8 8.5h8M8 11h5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  payment: `
    <svg viewBox="0 0 24 24" fill="none">
      <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" stroke-width="1.8" />
      <path d="M4 10h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M8.5 14.5c.7.9 1.8 1.4 3 1.4 1.7 0 3-1 3-2.4s-1.1-2-2.7-2.4c-1.5-.4-2.7-.8-2.7-2.2 0-1.2 1.2-2.1 2.8-2.1 1.1 0 2 .4 2.7 1.1" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`,
  handoff: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M7 6h6a3 3 0 0 1 3 3v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M13 11h4l-2-2m2 2-2 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M17 18H9a4 4 0 0 1-4-4v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M7 12l2 2-2 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`,
  followup: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 5a7 7 0 1 1-4.9 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M7 16V11h5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M12 5v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>`,
  knowledge: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M5 6.2A2.2 2.2 0 0 1 7.2 4h9.6A2.2 2.2 0 0 1 19 6.2v11.6A2.2 2.2 0 0 1 16.8 20H7.2A2.2 2.2 0 0 1 5 17.8V6.2Z" stroke="currentColor" stroke-width="1.8" />
      <path d="M8 8h5M8 11h8M8 14h6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  intake: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6 4h8l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M14 4v4h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M8 12h8M8 15h5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  tooth: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 4c2.8 0 5 2.1 5 4.7 0 1.4-.5 2.7-1.4 3.8-.7.8-1.1 1.7-1.4 2.7l-1.1 3.8c-.2.7-.7 1.2-1.3 1.2-.6 0-1.1-.4-1.3-1.1l-1.2-3.9c-.3-1-.8-1.9-1.4-2.7C7.5 11.4 7 10.1 7 8.7 7 6.1 9.2 4 12 4Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
    </svg>`,
  scales: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 4v14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M7 8h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M7 8l-3 5h6l-3-5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
      <path d="M17 8l-3 5h6l-3-5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
      <path d="M8 18h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>`,
  wrench: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M14.8 6.2a4.5 4.5 0 0 0-5.6 5.6L5 16l3 3 4.2-4.2a4.5 4.5 0 0 0 5.6-5.6l-2.5 2.5-2.5-2.5 2-2Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
    </svg>`,
  home: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M4 11.5 12 5l8 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M6.5 10.5V19h11V10.5" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M10 19v-5h4v5" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
    </svg>`,
  car: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M5 14.5 6.4 10.8A2 2 0 0 1 8.3 9.5h7.4a2 2 0 0 1 1.9 1.3L19 14.5" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M4 15.5h16v2.5H4z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <circle cx="7.5" cy="17.5" r="1.3" stroke="currentColor" stroke-width="1.8" />
      <circle cx="16.5" cy="17.5" r="1.3" stroke="currentColor" stroke-width="1.8" />
    </svg>`,
  scissors: `
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="6.5" cy="17.5" r="2" stroke="currentColor" stroke-width="1.7" />
      <circle cx="6.5" cy="6.5" r="2" stroke="currentColor" stroke-width="1.7" />
      <path d="M8 7.5 19 18.5M8 16.5 19 5.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  shopping: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6 7h13l-1.3 7.2a1.5 1.5 0 0 1-1.5 1.2H8.2a1.5 1.5 0 0 1-1.5-1.2L5.2 4.8H3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <circle cx="9" cy="19" r="1.4" stroke="currentColor" stroke-width="1.8" />
      <circle cx="17" cy="19" r="1.4" stroke="currentColor" stroke-width="1.8" />
    </svg>`,
  hotel: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6 20V6.5A2.5 2.5 0 0 1 8.5 4h7A2.5 2.5 0 0 1 18 6.5V20" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M8 8h8M8 11h8M8 14h8M8 17h8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  utensils: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6 4v7M8 4v7M7 11v9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M14 4v7c0 1.3 1 2.5 2 3v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M18 4v16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>`,
  alert: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 4 21 19H3L12 4Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M12 9v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
      <path d="M12 16h.01" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
    </svg>`,
  clipboard: `
    <svg viewBox="0 0 24 24" fill="none">
      <rect x="6" y="5" width="12" height="15" rx="2" stroke="currentColor" stroke-width="1.8" />
      <path d="M9 5.5h6V4h-6z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M9 10h6M9 13h5M9 16h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  bell: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 5a4 4 0 0 0-4 4v2c0 1.7-.6 2.8-1.6 4.1H17.6c-1-1.3-1.6-2.4-1.6-4.1V9a4 4 0 0 0-4-4Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M10 18a2 2 0 0 0 4 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
    </svg>`,
  document: `
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M6 4h8l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M14 4v4h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M8 11h6M8 14h8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
  default: `
    <svg viewBox="0 0 24 24" fill="none">
      <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.8" />
      <path d="M8 9h8M8 12h6M8 15h4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
    </svg>`,
}

function renderTemplateIcon(icon) {
  return TEMPLATE_ICON_SVGS[icon] || TEMPLATE_ICON_SVGS.default
}

function setWorkflow(record) {
  const normalized = record && Array.isArray(record.nodes) && Array.isArray(record.edges) && !record.steps
    ? {
        id: record.id || null,
        agent_id: record.agent_id || activeAgentId.value || null,
        name: record.name || 'Untitled workflow',
        description: record.description || '',
        status: record.status || 'draft',
        version: record.version || 1,
        nodes: clone(record.nodes || []),
        edges: clone(record.edges || []),
        settings: clone(record.settings || {}),
        validation_status: record.validation_status || 'draft',
      }
    : normalizeWorkflowRecord(record, activeAgentId.value)
  workflowMeta.id = normalized.id || null
  workflowMeta.agent_id = normalized.agent_id || activeAgentId.value
  workflowMeta.name = normalized.name || 'Untitled workflow'
  workflowMeta.description = normalized.description || ''
  workflowMeta.status = normalized.status || 'draft'
  workflowMeta.version = normalized.version || 1
  workflowMeta.validation_status = normalized.validation_status || 'draft'
  workflowMeta.settings = {
    snapToGrid: true,
    gridSize: 20,
    autoFit: true,
    ...(normalized.settings || {}),
  }

  nodes.value = clone(normalized.nodes || [])
  edges.value = clone(normalized.edges || [])
  selectedNodeId.value = nodes.value[0]?.id || null
  selectedFlowId.value = normalized.id || null
  syncInspectorFromSelection()
  resetHistory()
}

function createEditorWorkflow() {
  const draft = createBlankWorkflow(activeAgentId.value)
  setWorkflow({
    id: null,
    agent_id: activeAgentId.value,
    name: draft.name,
    description: draft.description,
    status: draft.status,
    version: draft.version,
    steps: serializeWorkflowDocument(draft),
  })
  showEmptyWorkflowPicker.value = false
}

function openWorkflowChooser() {
  selectedTemplateCategory.value = 'all'
  showEmptyWorkflowPicker.value = true
}

function closeWorkflowChooser() {
  showEmptyWorkflowPicker.value = false
}

function setTemplateCategory(category) {
  selectedTemplateCategory.value = category
}

function selectGalleryItem(item) {
  if (!item) return
  if (item.kind === 'empty') {
    createEditorWorkflow()
    return
  }
  loadTemplate(item.id || item.key)
}

function resetHistory() {
  history.value = [JSON.stringify(serializeWorkflowDocument(composeWorkflowDocument()))]
  future.value = []
  if (historyTimer.value) {
    clearTimeout(historyTimer.value)
    historyTimer.value = null
  }
}

function queueHistorySnapshot() {
  if (historyTimer.value) {
    clearTimeout(historyTimer.value)
  }
  historyTimer.value = setTimeout(() => {
    const snapshot = JSON.stringify(serializeWorkflowDocument(composeWorkflowDocument()))
    const last = history.value[history.value.length - 1]
    if (snapshot !== last) {
      history.value.push(snapshot)
      if (history.value.length > 40) history.value.shift()
      future.value = []
    }
  }, 120)
}

function restoreSnapshot(snapshot) {
  if (!snapshot) return
  const parsed = JSON.parse(snapshot)
  workflowMeta.id = parsed.workflow?.id || null
  workflowMeta.agent_id = parsed.workflow?.agent_id || activeAgentId.value
  workflowMeta.name = parsed.workflow?.name || 'Untitled workflow'
  workflowMeta.description = parsed.workflow?.description || ''
  workflowMeta.status = parsed.workflow?.status || 'draft'
  workflowMeta.version = parsed.workflow?.version || 1
  workflowMeta.validation_status = parsed.workflow?.validation_status || 'draft'
  workflowMeta.settings = {
    snapToGrid: true,
    gridSize: 20,
    autoFit: true,
    ...(parsed.settings || {}),
  }
  nodes.value = clone(parsed.nodes || [])
  edges.value = clone(parsed.edges || [])
  selectedNodeId.value = nodes.value[0]?.id || null
  syncInspectorFromSelection()
}

function undo() {
  if (history.value.length < 2) return
  const current = history.value.pop()
  future.value.push(current)
  restoreSnapshot(history.value[history.value.length - 1])
}

function redo() {
  if (!future.value.length) return
  const next = future.value.pop()
  history.value.push(next)
  restoreSnapshot(next)
}

function syncInspectorFromSelection() {
  const node = selectedNode.value
  if (!node) {
    inspectorDraft.title = ''
    inspectorDraft.description = ''
    inspectorDraft.kind = 'message'
    inspectorDraft.text = ''
    inspectorDraft.greeting = ''
    inspectorDraft.language = 'english'
    inspectorDraft.voiceLabel = ''
    inspectorDraft.providerVoiceId = ''
    inspectorDraft.speakingStyle = 'clear'
    inspectorDraft.fieldsText = 'name, phone, service'
    inspectorDraft.requiredFieldsText = 'name, phone'
    inspectorDraft.prompt = ''
    inspectorDraft.branchesText = 'Yes: yes, book, ready\nNo: no, later, not now'
    inspectorDraft.baseName = ''
    inspectorDraft.fallbackMessage = ''
    inspectorDraft.service = ''
    inspectorDraft.duration = 30
    inspectorDraft.calendar = ''
    inspectorDraft.confirmationChannel = 'sms'
    inspectorDraft.paymentMode = 'deposit'
    inspectorDraft.amount = 50
    inspectorDraft.percentage = 20
    inspectorDraft.currency = 'USD'
    inspectorDraft.paymentMessage = ''
    inspectorDraft.transferTarget = ''
    inspectorDraft.transferCondition = ''
    inspectorDraft.transferAnnounce = ''
    inspectorDraft.channel = 'sms'
    inspectorDraft.subject = ''
    inspectorDraft.template = ''
    inspectorDraft.method = 'POST'
    inspectorDraft.url = ''
    inspectorDraft.secret = ''
    inspectorDraft.closing = ''
    inspectorDraft.voiceTone = 'professional'
    return
  }

  const cfg = node.data?.config || {}
  inspectorDraft.kind = node.data?.kind || 'message'
  inspectorDraft.title = node.data?.title || ''
  inspectorDraft.description = node.data?.description || ''
  inspectorDraft.text = cfg.text || ''
  inspectorDraft.greeting = cfg.greeting || ''
  inspectorDraft.language = cfg.language || 'english'
  inspectorDraft.voiceLabel = cfg.voiceLabel || ''
  inspectorDraft.providerVoiceId = cfg.providerVoiceId || ''
  inspectorDraft.speakingStyle = cfg.speakingStyle || 'clear'
  inspectorDraft.fieldsText = Array.isArray(cfg.fields) ? cfg.fields.join(', ') : 'name, phone, service'
  inspectorDraft.requiredFieldsText = Array.isArray(cfg.requiredFields) ? cfg.requiredFields.join(', ') : 'name, phone'
  inspectorDraft.prompt = cfg.prompt || ''
  inspectorDraft.branchesText = Array.isArray(cfg.branches)
    ? cfg.branches.map((branch) => `${branch.label}: ${(branch.keywords || []).join(', ')}`).join('\n')
    : 'Yes: yes, book, ready\nNo: no, later, not now'
  inspectorDraft.baseName = cfg.baseName || ''
  inspectorDraft.fallbackMessage = cfg.fallbackMessage || ''
  inspectorDraft.service = cfg.service || ''
  inspectorDraft.duration = Number(cfg.duration || 30)
  inspectorDraft.calendar = cfg.calendar || ''
  inspectorDraft.confirmationChannel = cfg.confirmationChannel || 'sms'
  inspectorDraft.paymentMode = cfg.mode || 'deposit'
  inspectorDraft.amount = Number(cfg.amount || 50)
  inspectorDraft.percentage = Number(cfg.percentage || 20)
  inspectorDraft.currency = cfg.currency || 'USD'
  inspectorDraft.paymentMessage = cfg.message || ''
  inspectorDraft.transferTarget = cfg.target || ''
  inspectorDraft.transferCondition = cfg.condition || ''
  inspectorDraft.transferAnnounce = cfg.announce || ''
  inspectorDraft.channel = cfg.channel || 'sms'
  inspectorDraft.subject = cfg.subject || ''
  inspectorDraft.template = cfg.template || ''
  inspectorDraft.method = cfg.method || 'POST'
  inspectorDraft.url = cfg.url || ''
  inspectorDraft.secret = cfg.secret || ''
  inspectorDraft.closing = cfg.closing || ''
  inspectorDraft.voiceTone = cfg.voiceTone || 'professional'
}

function applyInspectorChanges() {
  const node = selectedNode.value
  if (!node) return

  node.data.title = inspectorDraft.title.trim() || 'Untitled block'
  node.data.description = inspectorDraft.description.trim()
  node.data.kind = inspectorDraft.kind
  node.data.config = node.data.config || {}
  node.data.config.language = inspectorDraft.language
  node.data.config.voiceLabel = inspectorDraft.voiceLabel.trim()
  node.data.config.providerVoiceId = inspectorDraft.providerVoiceId.trim()
  node.data.config.speakingStyle = inspectorDraft.speakingStyle
  node.data.config.voiceTone = inspectorDraft.voiceTone

  if (node.data.kind === 'start') {
    node.data.config.greeting = inspectorDraft.greeting.trim()
  } else if (node.data.kind === 'message') {
    node.data.config.text = inspectorDraft.text.trim()
  } else if (node.data.kind === 'voice') {
    node.data.config.language = inspectorDraft.language
    node.data.config.voiceLabel = inspectorDraft.voiceLabel.trim()
    node.data.config.providerVoiceId = inspectorDraft.providerVoiceId.trim()
    node.data.config.speakingStyle = inspectorDraft.speakingStyle
  } else if (node.data.kind === 'collect') {
    node.data.config.fields = parseList(inspectorDraft.fieldsText)
    node.data.config.requiredFields = parseList(inspectorDraft.requiredFieldsText)
    node.data.config.prompt = inspectorDraft.prompt.trim()
  } else if (node.data.kind === 'decision') {
    node.data.config.branches = parseBranches(inspectorDraft.branchesText)
  } else if (node.data.kind === 'knowledge') {
    node.data.config.baseName = inspectorDraft.baseName.trim()
    node.data.config.fallbackMessage = inspectorDraft.fallbackMessage.trim()
  } else if (node.data.kind === 'booking') {
    node.data.config.service = inspectorDraft.service.trim()
    node.data.config.duration = Number(inspectorDraft.duration || 0)
    node.data.config.calendar = inspectorDraft.calendar.trim()
    node.data.config.confirmationChannel = inspectorDraft.confirmationChannel
  } else if (node.data.kind === 'payment') {
    node.data.config.mode = inspectorDraft.paymentMode
    node.data.config.amount = Number(inspectorDraft.amount || 0)
    node.data.config.percentage = Number(inspectorDraft.percentage || 0)
    node.data.config.currency = inspectorDraft.currency.trim() || 'USD'
    node.data.config.message = inspectorDraft.paymentMessage.trim()
  } else if (node.data.kind === 'transfer') {
    node.data.config.target = inspectorDraft.transferTarget.trim()
    node.data.config.condition = inspectorDraft.transferCondition.trim()
    node.data.config.announce = inspectorDraft.transferAnnounce.trim()
  } else if (node.data.kind === 'contact') {
    node.data.config.channel = inspectorDraft.channel
    node.data.config.subject = inspectorDraft.subject.trim()
    node.data.config.template = inspectorDraft.template.trim()
  } else if (node.data.kind === 'webhook') {
    node.data.config.method = inspectorDraft.method
    node.data.config.url = inspectorDraft.url.trim()
    node.data.config.secret = inspectorDraft.secret.trim()
  } else if (node.data.kind === 'end') {
    node.data.config.closing = inspectorDraft.closing.trim()
  }

  node.data.summary = buildNodeSummary(node)
  queueHistorySnapshot()
}

function buildNodeSummary(node) {
  const cfg = node.data?.config || {}
  switch (node.data?.kind) {
    case 'start':
      return cfg.greeting || 'Open the call.'
    case 'message':
      return cfg.text || 'Scripted message.'
    case 'voice':
      return [cfg.language, cfg.voiceLabel || cfg.providerVoiceId].filter(Boolean).join(' · ') || 'Voice settings.'
    case 'collect':
      return Array.isArray(cfg.fields) ? `Fields: ${cfg.fields.join(', ')}` : 'Collect key details.'
    case 'decision':
      return Array.isArray(cfg.branches) ? `${cfg.branches.length} branches` : 'Decision with branches.'
    case 'knowledge':
      return cfg.baseName ? `Knowledge base: ${cfg.baseName}` : 'Use your knowledge base.'
    case 'booking':
      return `${cfg.service || 'Booking'} · ${cfg.duration || 30} min`
    case 'payment':
      return cfg.mode === 'deposit'
        ? `Deposit: ${cfg.amount || cfg.percentage || 0}${cfg.currency || ''}`
        : `Full payment: ${cfg.amount || 0}${cfg.currency || ''}`
    case 'transfer':
      return cfg.target ? `Transfer to ${cfg.target}` : 'Transfer to a human.'
    case 'contact':
      return `${(cfg.channel || 'sms').toUpperCase()} follow-up`
    case 'webhook':
      return cfg.url || 'External API action'
    case 'end':
      return cfg.closing || 'Close the conversation.'
    default:
      return ''
  }
}

function updateNodeSummary(node) {
  node.data.summary = buildNodeSummary(node)
}

function addNode(kind, options = {}) {
  const anchor = selectedNode.value
  const basePosition = options.position || (anchor
    ? {
        x: Number(anchor.position?.x || 0) + 320,
        y: Number(anchor.position?.y || 0) + (anchor.data?.kind === 'decision' ? 160 : 20),
      }
    : {
        x: 280 + nodes.value.length * 12,
        y: 140 + nodes.value.length * 18,
      })
  const node = createNode(kind, basePosition, options.overrides || {})
  updateNodeSummary(node)
  nodes.value = [...nodes.value, node]

  if (anchor && options.connect !== false) {
    edges.value = addEdge(
      {
        source: anchor.id,
        target: node.id,
        sourceHandle: anchor.data?.kind === 'decision' ? 'yes' : 'out',
        targetHandle: node.data?.kind === 'end' ? 'in' : 'in',
        label: options.label || 'Next',
        type: 'smoothstep',
        markerEnd: { type: MarkerType.Arrow },
      },
      edges.value
    )
  }

  selectedNodeId.value = node.id
  syncInspectorFromSelection()
  queueHistorySnapshot()
}

function duplicateNode() {
  if (!selectedNode.value) return
  const copy = cloneNode(selectedNode.value, { x: 300, y: 40 })
  updateNodeSummary(copy)
  nodes.value = [...nodes.value, copy]
  selectedNodeId.value = copy.id
  syncInspectorFromSelection()
  queueHistorySnapshot()
}

function removeNode() {
  if (!selectedNode.value) return
  const id = selectedNode.value.id
  nodes.value = nodes.value.filter((node) => node.id !== id)
  edges.value = edges.value.filter((edge) => edge.source !== id && edge.target !== id)
  selectedNodeId.value = nodes.value[0]?.id || null
  syncInspectorFromSelection()
  queueHistorySnapshot()
}

function onConnect(connection) {
  edges.value = addEdge(
    {
      ...connection,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.Arrow },
      label: connection.sourceHandle === 'yes' ? 'Yes' : connection.sourceHandle === 'no' ? 'No' : 'Next',
    },
    edges.value
  )
  queueHistorySnapshot()
}

function onNodesChange(changes) {
  nodes.value = applyNodeChanges(changes, nodes.value)
  queueHistorySnapshot()
}

function onEdgesChange(changes) {
  edges.value = applyEdgeChanges(changes, edges.value)
  queueHistorySnapshot()
}

function onPaneClick() {
  selectedNodeId.value = null
  syncInspectorFromSelection()
}

function onNodeClick(payload) {
  const node = payload?.node
  if (!node?.id) return
  selectedNodeId.value = node.id
  selectedNodeKind.value = node.data?.kind || 'message'
  syncInspectorFromSelection()
}

function onEdgeClick(payload) {
  const edge = payload?.edge
  if (!edge?.source) return
  selectedNodeId.value = edge.source
  syncInspectorFromSelection()
}

function onDragOver(event) {
  event.preventDefault()
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

function onDrop(event) {
  event.preventDefault()
  const kind = event.dataTransfer?.getData('application/vueflow') || event.dataTransfer?.getData('text/plain')
  if (!kind) return
  const position = screenToFlowCoordinate({
    x: event.clientX,
    y: event.clientY,
  })
  addNode(kind, { position, connect: false })
}

function appendFromPalette(kind) {
  addNode(kind, { connect: true })
}

function loadTemplate(templateKey) {
  const workflow = buildWorkflowFromTemplate(templateKey, activeAgentId.value)
  if (!workflow) return
  setWorkflow({
    ...workflow,
    steps: serializeWorkflowDocument(workflow),
  })
  selectedFlowId.value = null
  closeWorkflowChooser()
  nextTick(() => fitView?.())
}

function autoArrange() {
  const arranged = autoLayoutWorkflow(composeWorkflowDocument())
  nodes.value = clone(arranged.nodes || [])
  edges.value = clone(arranged.edges || [])
  queueHistorySnapshot()
  nextTick(() => fitView?.())
}

function clearCanvas() {
  const draft = createBlankWorkflow(activeAgentId.value)
  setWorkflow({
    ...draft,
    steps: serializeWorkflowDocument(draft),
  })
  closeWorkflowChooser()
}

function composeWorkflowDocument() {
  return {
    id: workflowMeta.id,
    agent_id: activeAgentId.value || workflowMeta.agent_id || null,
    name: workflowMeta.name,
    description: workflowMeta.description,
    status: workflowMeta.status,
    version: workflowMeta.version,
    nodes: clone(nodes.value),
    edges: clone(edges.value),
    settings: clone(workflowMeta.settings),
  }
}

function exportPayload() {
  return serializeWorkflowDocument({
    ...composeWorkflowDocument(),
    validation_status: validationStatus.value,
  })
}

async function refreshFlows() {
  if (!activeAgentId.value) {
    flows.value = []
    showEmptyWorkflowPicker.value = true
    return
  }
  isLoading.value = true
  loadError.value = ''
  try {
    const response = await flowApi.getAll(activeAgentId.value)
    const list = Array.isArray(response) ? response : response?.data || response?.flows || []
    flows.value = list.map((record) => normalizeWorkflowRecord(record, activeAgentId.value))

    const activeWorkflowId = workflowMeta.id || selectedFlowId.value
    const previouslySelected = flows.value.find((flow) => String(flow.id) === String(activeWorkflowId))
    if (previouslySelected) {
      setWorkflow(previouslySelected)
    } else if (flows.value.length) {
      setWorkflow(flows.value[0])
      showEmptyWorkflowPicker.value = false
    } else {
      clearCanvas()
      showEmptyWorkflowPicker.value = true
    }
  } catch (error) {
    loadError.value = error?.message || 'Unable to load workflows.'
    clearCanvas()
    showEmptyWorkflowPicker.value = true
  } finally {
    isLoading.value = false
  }
}

function selectFlow(flow) {
  if (!flow?.id) return
  selectedFlowId.value = flow.id
  workflowMeta.id = flow.id
  setWorkflow(flow)
  closeWorkflowChooser()
  nextTick(() => fitView?.())
}

async function saveWorkflow(nextStatus = workflowMeta.status, successMessage = 'Workflow saved.') {
  if (!activeAgentId.value) return
  isSaving.value = true
  saveMessage.value = ''
  const previousStatus = workflowMeta.status
  const normalizedStatus = String(nextStatus || 'draft').toLowerCase()
  workflowMeta.status = normalizedStatus
  try {
    const payload = {
      agent_id: activeAgentId.value,
      name: workflowMeta.name,
      steps: JSON.stringify(exportPayload()),
      status: normalizedStatus,
    }
    const activeWorkflowId = workflowMeta.id || selectedFlowId.value
    let response
    if (activeWorkflowId) {
      response = await flowApi.update(activeWorkflowId, payload)
    } else {
      response = await flowApi.create(payload)
    }

    const returnedId = response?.id || response?.data?.id || response?.flow?.id || activeWorkflowId
    const returnedStatus = response?.status || response?.data?.status || normalizedStatus
    if (returnedId) {
      workflowMeta.id = returnedId
      selectedFlowId.value = returnedId
    }
    workflowMeta.status = returnedStatus

    saveMessage.value = successMessage
    await refreshFlows()
  } catch (error) {
    workflowMeta.status = previousStatus
    saveMessage.value = error?.message || 'Unable to save workflow.'
  } finally {
    isSaving.value = false
  }
}

async function saveDraft() {
  await saveWorkflow('draft', 'Workflow saved as draft.')
}

async function activateWorkflow() {
  await saveWorkflow('active', 'Workflow activated.')
}

async function archiveWorkflow() {
  await saveWorkflow('archived', 'Workflow archived.')
}

async function testWorkflow() {
  const workflowId = workflowMeta.id || selectedFlowId.value
  const payload = {
    message: simulationInput.value,
    currentState: null,
    channel: 'chat',
    promptMode: 'default',
    agent_id: activeAgentId.value,
  }

  try {
    if (workflowId) {
      simulationOutput.value = await flowApi.simulate(workflowId, payload)
      return
    }
  } catch (error) {
    console.warn('Backend workflow simulation failed, falling back to local simulation:', error)
  }

  simulationOutput.value = simulateWorkflow(composeWorkflowDocument(), simulationInput.value)
}

function fitCanvas() {
  nextTick(() => fitView?.())
}

function handleKeyboardShortcuts(event) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
    event.preventDefault()
    undo()
  } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && event.shiftKey) {
    event.preventDefault()
    redo()
  } else if (event.key === 'Delete' || event.key === 'Backspace') {
    if (hasSelection.value) {
      event.preventDefault()
      removeNode()
    }
  } else if (event.key === 'Escape') {
    selectedNodeId.value = null
    syncInspectorFromSelection()
  }
}

watch(
  () => route.fullPath,
  () => {
    activeAgentId.value = resolveActiveAgentId()
    refreshFlows()
  }
)

watch(
  () => activeAgentId.value,
  () => {
    refreshFlows()
  }
)

watch(
  () => selectedNodeId.value,
  () => {
    syncInspectorFromSelection()
  }
)

watch(
  () => [nodes.value, edges.value, workflowMeta.name, workflowMeta.description, workflowMeta.status, workflowMeta.version],
  () => {
    queueHistorySnapshot()
  },
  { deep: true }
)

watch(
  () => selectedNode.value,
  () => {
    syncInspectorFromSelection()
  }
)

onMounted(() => {
  activeAgentId.value = resolveActiveAgentId()
  if (activeAgentId.value) {
    refreshFlows()
  } else {
    clearCanvas()
    showEmptyWorkflowPicker.value = true
  }
  window.addEventListener('keydown', handleKeyboardShortcuts)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyboardShortcuts)
  if (historyTimer.value) clearTimeout(historyTimer.value)
})
</script>

<template>
  <div class="workflow-builder-shell">
    <header class="workflow-builder-header">
      <div>
        <div class="workflow-builder-eyebrow">Workflows / Canvas</div>
        <h1 class="workflow-builder-title">Design the conversation flow</h1>
        <p class="workflow-builder-subtitle">
          Build the path your agent follows: greet, collect details, decide, book, request payment, or transfer to a human.
        </p>
      </div>

      <div class="workflow-builder-header-actions">
        <div class="workflow-builder-header-actions__status">
          <div class="workflow-status" :class="`workflow-status--${workflowStatusTone}`">
            {{ workflowStatusLabel }}
          </div>
          <div class="workflow-status" :class="`workflow-status--${validationTone}`">
            {{ validationStatus.toUpperCase() }}
          </div>
        </div>
        <button type="button" class="tool-button" @click="fitCanvas">Fit view</button>
        <button type="button" class="tool-button" @click="autoArrange">Auto layout</button>
        <button type="button" class="tool-button" @click="undo" :disabled="history.length < 2">Undo</button>
        <button type="button" class="tool-button" @click="redo" :disabled="!future.length">Redo</button>
        <button type="button" class="secondary-button" @click="saveDraft" :disabled="isSaving || !activeAgentId">
          {{ isSaving ? 'Saving...' : 'Save as draft' }}
        </button>
        <button type="button" class="primary-button" @click="activateWorkflow" :disabled="isSaving || !activeAgentId">
          {{ isSaving ? 'Saving...' : 'Activate workflow' }}
        </button>
        <button type="button" class="tool-button" @click="archiveWorkflow" :disabled="isSaving || !activeAgentId">
          Archive workflow
        </button>
      </div>
    </header>

    <div class="workflow-builder-body">
      <aside class="workflow-sidebar workflow-sidebar--left">
        <section class="sidebar-section">
          <div class="sidebar-section__title">Agent</div>
          <div class="agent-card">
            <div class="agent-card__label">Active agent</div>
            <div class="agent-card__value">{{ activeAgentId || 'No agent selected' }}</div>
            <div class="agent-card__meta">
              {{ selectedFlow?.name || workflowMeta.name }}
            </div>
          </div>
        </section>

        <section class="sidebar-section">
          <div class="sidebar-section__title">Workflows</div>
          <div v-if="hasWorkflows" class="workflow-list">
            <button
              v-for="flow in flows"
              :key="flow.id"
              type="button"
              class="workflow-list-item"
              :class="{ 'is-active': String(flow.id) === String(selectedFlowId) }"
              @click="selectFlow(flow)"
            >
              <div class="workflow-list-item__name">{{ flow.name }}</div>
              <div class="workflow-list-item__meta">
                v{{ flow.version || 1 }} · {{ String(flow.status || 'draft').toUpperCase() }}
              </div>
            </button>
          </div>
          <div v-else class="workflow-empty-state">
            <strong>No workflow yet</strong>
            <p>Click New workflow to choose a template or start from a blank flow.</p>
            <div class="workflow-empty-state__actions">
              <button type="button" class="secondary-button" @click="openWorkflowChooser">New workflow</button>
            </div>
          </div>
          <button type="button" class="secondary-button" @click="openWorkflowChooser">
            + New workflow
          </button>
        </section>

        <section class="sidebar-section">
          <div class="sidebar-section__title">Blocks</div>
          <div class="palette-grid">
            <button
              v-for="block in availableBlocks"
              :key="block.type"
              type="button"
              class="palette-card"
              draggable="true"
              @click="appendFromPalette(block.type)"
              @dragstart="(event) => event.dataTransfer?.setData('application/vueflow', block.type)"
            >
              <span class="palette-card__icon">{{ block.icon }}</span>
              <span class="palette-card__text">
                <strong>{{ block.label }}</strong>
                <small>{{ block.description }}</small>
              </span>
            </button>
          </div>
        </section>

        <section class="sidebar-section">
          <div class="sidebar-section__title">Templates</div>
          <div class="template-tabs">
            <button
              v-for="category in templateCategories"
              :key="category.id"
              type="button"
              class="template-tab"
              :class="{ 'is-active': selectedTemplateCategory === category.id }"
              @click="setTemplateCategory(category.id)"
            >
              {{ category.label }}
            </button>
          </div>
          <div class="template-list">
            <button
              v-for="template in filteredTemplates"
              :key="template.id || template.key"
              type="button"
              class="template-card"
              :class="{ 'template-card--blank': template.kind === 'empty' }"
              @click="selectGalleryItem(template)"
            >
              <span class="template-card__icon" aria-hidden="true" v-html="renderTemplateIcon(template.icon)" />
              <span class="template-card__body">
                <strong>{{ template.name }}</strong>
                <span>{{ template.description }}</span>
                <div v-if="template.tags && template.tags.length" class="template-card__tags">
                  <span v-for="tag in template.tags.slice(0, 3)" :key="tag" class="template-chip">{{ tag }}</span>
                </div>
              </span>
              <span class="template-card__cta">{{ template.kind === 'empty' ? 'Open blank canvas →' : 'Use template →' }}</span>
            </button>
          </div>
        </section>
      </aside>

      <main class="workflow-canvas-panel">
        <div class="workflow-canvas-toolbar">
          <div class="workflow-canvas-toolbar__group">
            <label class="field-inline">
              Name
              <input v-model="workflowMeta.name" type="text" placeholder="Workflow name" />
            </label>
            <label class="field-inline">
              Description
              <input v-model="workflowMeta.description" type="text" placeholder="What this workflow does" />
            </label>
          </div>
            <div class="workflow-canvas-toolbar__group">
              <button type="button" class="tool-button" @click="clearCanvas">Reset</button>
              <button type="button" class="tool-button" @click="saveDraft" :disabled="isSaving || !activeAgentId">
                {{ isSaving ? 'Saving...' : 'Save as draft' }}
              </button>
            </div>
          </div>

        <div class="canvas-stage" @dragover="onDragOver" @drop="onDrop">
          <div v-if="showEmptyWorkflowState" class="canvas-empty-state">
            <div class="canvas-empty-state__hero">
              <div class="canvas-empty-state__eyebrow">No workflow found</div>
              <h2>Start from a template or a blank flow</h2>
              <p>
                Pick a ready-made workflow for a clinic, law firm, plumber, or real estate business, or create an empty workflow and build it step by step.
              </p>
            </div>

            <div class="template-tabs template-tabs--overlay">
              <button
                v-for="category in templateCategories"
                :key="category.id"
                type="button"
                class="template-tab"
                :class="{ 'is-active': selectedTemplateCategory === category.id }"
                @click="setTemplateCategory(category.id)"
              >
                {{ category.label }}
              </button>
            </div>

            <div class="canvas-empty-state__choices">
              <button
                v-for="template in filteredTemplates"
                :key="template.id || template.key"
                type="button"
                class="canvas-choice-card"
                :class="{ 'canvas-choice-card--blank': template.kind === 'empty' }"
                @click="selectGalleryItem(template)"
              >
                <span class="canvas-choice-card__icon" aria-hidden="true" v-html="renderTemplateIcon(template.icon)" />
                <span class="canvas-choice-card__content">
                  <strong>{{ template.name }}</strong>
                  <span>{{ template.description }}</span>
                  <div v-if="template.tags && template.tags.length" class="canvas-choice-card__tags">
                    <span v-for="tag in template.tags.slice(0, 3)" :key="tag" class="template-chip">{{ tag }}</span>
                  </div>
                </span>
                <span class="canvas-choice-card__cta">{{ template.kind === 'empty' ? 'Open blank canvas →' : 'Use template →' }}</span>
              </button>
            </div>
          </div>
          <VueFlow
            v-model:nodes="nodes"
            v-model:edges="edges"
            :node-types="nodeTypes"
            :default-edge-options="defaultEdgeOptions"
            :snap-to-grid="workflowMeta.settings.snapToGrid"
            :snap-grid="[workflowMeta.settings.gridSize, workflowMeta.settings.gridSize]"
            class="canvas-flow"
            @connect="onConnect"
            @nodes-change="onNodesChange"
            @edges-change="onEdgesChange"
            @pane-click="onPaneClick"
            @node-click="onNodeClick"
            @edge-click="onEdgeClick"
          >
            <Background variant="dots" :gap="24" :size="1" color="#dbe4f0" />
            <MiniMap node-color="#94a3b8" mask-color="rgba(15, 23, 42, 0.15)" />
            <Controls />

            <template #panel-top-left>
              <div class="canvas-hint">
                Drag blocks from the left or click one to add it. Select a block to edit its details.
              </div>
            </template>
          </VueFlow>
        </div>

        <div class="workflow-status-strip">
          <div class="workflow-status-strip__item">
            <strong>{{ nodes.length }}</strong>
            <span>Blocks</span>
          </div>
          <div class="workflow-status-strip__item">
            <strong>{{ edges.length }}</strong>
            <span>Connections</span>
          </div>
          <div class="workflow-status-strip__item">
            <strong>{{ validationIssues.length }}</strong>
            <span>Issues</span>
          </div>
          <div class="workflow-status-strip__item">
            <strong>{{ workflowMeta.version }}</strong>
            <span>Version</span>
          </div>
        </div>
      </main>

      <aside class="workflow-sidebar workflow-sidebar--right">
        <section class="sidebar-section">
          <div class="sidebar-section__title">Selected block</div>
          <div v-if="hasSelection" class="inspector-card">
            <div class="inspector-card__header">
              <div>
                <div class="inspector-card__kind">{{ selectedNode?.data?.kind || 'block' }}</div>
                <div class="inspector-card__title">{{ selectedNodeLabel }}</div>
              </div>
              <button type="button" class="tool-button" @click="duplicateNode">Duplicate</button>
            </div>

            <label class="field-block">
              Title
              <input v-model="inspectorDraft.title" type="text" />
            </label>

            <label class="field-block">
              Description
              <textarea v-model="inspectorDraft.description" rows="3" />
            </label>

            <label class="field-block">
              Type
              <select v-model="inspectorDraft.kind">
                <option v-for="block in availableBlocks" :key="block.type" :value="block.type">{{ block.label }}</option>
              </select>
            </label>

            <template v-if="inspectorDraft.kind === 'start'">
              <label class="field-block">
                Custom greeting
                <textarea v-model="inspectorDraft.greeting" rows="3" />
              </label>
              <label class="field-block">
                Language
                <input v-model="inspectorDraft.language" type="text" placeholder="english" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'message'">
              <label class="field-block">
                Message text
                <textarea v-model="inspectorDraft.text" rows="4" />
              </label>
              <label class="field-block">
                Tone
                <input v-model="inspectorDraft.voiceTone" type="text" placeholder="professional" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'voice'">
              <label class="field-block">
                Language
                <input v-model="inspectorDraft.language" type="text" placeholder="english" />
              </label>
              <label class="field-block">
                Voice label
                <input v-model="inspectorDraft.voiceLabel" type="text" placeholder="Warm female voice" />
              </label>
              <label class="field-block">
                Provider voice ID
                <input v-model="inspectorDraft.providerVoiceId" type="text" placeholder="elevenlabs_voice_id" />
              </label>
              <label class="field-block">
                Speaking style
                <input v-model="inspectorDraft.speakingStyle" type="text" placeholder="clear" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'collect'">
              <label class="field-block">
                Fields to collect
                <textarea v-model="inspectorDraft.fieldsText" rows="3" />
              </label>
              <label class="field-block">
                Required fields
                <textarea v-model="inspectorDraft.requiredFieldsText" rows="2" />
              </label>
              <label class="field-block">
                Prompt
                <textarea v-model="inspectorDraft.prompt" rows="3" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'decision'">
              <label class="field-block">
                Branches
                <textarea
                  v-model="inspectorDraft.branchesText"
                  rows="5"
                  placeholder="Yes: yes, book, ready&#10;No: no, later, not now"
                />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'knowledge'">
              <label class="field-block">
                Knowledge base
                <input v-model="inspectorDraft.baseName" type="text" placeholder="Dental FAQ" />
              </label>
              <label class="field-block">
                Fallback message
                <textarea v-model="inspectorDraft.fallbackMessage" rows="3" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'booking'">
              <label class="field-block">
                Service
                <input v-model="inspectorDraft.service" type="text" />
              </label>
              <label class="field-block">
                Duration (minutes)
                <input v-model.number="inspectorDraft.duration" type="number" min="5" step="5" />
              </label>
              <label class="field-block">
                Calendar
                <input v-model="inspectorDraft.calendar" type="text" />
              </label>
              <label class="field-block">
                Confirmation channel
                <select v-model="inspectorDraft.confirmationChannel">
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'payment'">
              <label class="field-block">
                Payment mode
                <select v-model="inspectorDraft.paymentMode">
                  <option value="deposit">Deposit</option>
                  <option value="full">Full payment</option>
                </select>
              </label>
              <label class="field-block">
                Amount
                <input v-model.number="inspectorDraft.amount" type="number" min="0" step="1" />
              </label>
              <label class="field-block">
                Percentage
                <input v-model.number="inspectorDraft.percentage" type="number" min="0" step="1" />
              </label>
              <label class="field-block">
                Currency
                <input v-model="inspectorDraft.currency" type="text" placeholder="USD" />
              </label>
              <label class="field-block">
                Payment message
                <textarea v-model="inspectorDraft.paymentMessage" rows="3" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'transfer'">
              <label class="field-block">
                Transfer target
                <input v-model="inspectorDraft.transferTarget" type="text" />
              </label>
              <label class="field-block">
                Condition
                <textarea v-model="inspectorDraft.transferCondition" rows="2" />
              </label>
              <label class="field-block">
                Transfer message
                <textarea v-model="inspectorDraft.transferAnnounce" rows="3" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'contact'">
              <label class="field-block">
                Channel
                <select v-model="inspectorDraft.channel">
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </label>
              <label class="field-block">
                Subject
                <input v-model="inspectorDraft.subject" type="text" />
              </label>
              <label class="field-block">
                Template
                <textarea v-model="inspectorDraft.template" rows="3" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'webhook'">
              <label class="field-block">
                Method
                <input v-model="inspectorDraft.method" type="text" placeholder="POST" />
              </label>
              <label class="field-block">
                URL
                <input v-model="inspectorDraft.url" type="text" placeholder="https://..." />
              </label>
              <label class="field-block">
                Secret
                <input v-model="inspectorDraft.secret" type="text" placeholder="Optional" />
              </label>
            </template>

            <template v-else-if="inspectorDraft.kind === 'end'">
              <label class="field-block">
                Closing line
                <textarea v-model="inspectorDraft.closing" rows="3" />
              </label>
            </template>

            <div class="inspector-actions">
              <button type="button" class="secondary-button" @click="applyInspectorChanges">Apply changes</button>
              <button type="button" class="secondary-button" @click="removeNode">Delete</button>
              <button type="button" class="secondary-button" @click="appendFromPalette(inspectorDraft.kind)">Add another</button>
            </div>
          </div>

          <div v-else class="inspector-empty">
            Select a block to edit its details.
          </div>
        </section>

        <section class="sidebar-section">
          <div class="sidebar-section__title">Workflow issues</div>
          <div class="issues-list">
            <div
              v-for="issue in validationIssues"
              :key="issue.code"
              class="issue-card"
              :class="`issue-card--${issue.level}`"
            >
              <strong>{{ issue.title }}</strong>
              <span>{{ issue.detail }}</span>
            </div>
            <div v-if="!validationIssues.length" class="issues-empty">No validation issues detected.</div>
          </div>
        </section>

        <section class="sidebar-section">
          <div class="sidebar-section__title">Test workflow</div>
          <div class="simulation-card">
            <div v-if="isDraftWorkflow" class="simulation-warning">
              This workflow is not active yet. It will not affect live calls until activated.
            </div>
            <label class="field-block">
              Example message
              <textarea v-model="simulationInput" rows="4" placeholder="Example caller text" />
            </label>
            <button type="button" class="primary-button" @click="testWorkflow">Simulate call</button>

            <div v-if="simulationOutput" class="simulation-output">
              <div class="simulation-output__row">
                <strong>Active block</strong>
                <span>{{ simulationOutput.activeNode?.title || 'Unknown' }}</span>
              </div>
              <div class="simulation-output__row">
                <strong>Branch</strong>
                <span>{{ simulationOutput.branch || 'Default path' }}</span>
              </div>
              <div class="simulation-output__row">
                <strong>Missing info</strong>
                <span>{{ simulationOutput.missingFields.length ? simulationOutput.missingFields.join(', ') : 'None' }}</span>
              </div>
              <div class="simulation-output__row">
                <strong>Response</strong>
                <span>{{ simulationOutput.response }}</span>
              </div>
              <div class="simulation-output__row">
                <strong>Action</strong>
                <span>{{ simulationOutput.actions.at(-1)?.action || 'None' }}</span>
              </div>
            </div>
          </div>
        </section>
      </aside>
    </div>

    <footer class="workflow-builder-footer">
      <span v-if="loadError" class="status-text status-text--error">{{ loadError }}</span>
      <span v-else-if="saveMessage" class="status-text">{{ saveMessage }}</span>
      <span v-else class="status-text">Connected to agent {{ activeAgentId || 'none' }}.</span>
    </footer>
  </div>
</template>

<style>
@import '@vue-flow/core/dist/style.css';
@import '@vue-flow/core/dist/theme-default.css';
@import '@vue-flow/controls/dist/style.css';
@import '@vue-flow/minimap/dist/style.css';
</style>

<style scoped>
.workflow-builder-shell {
  min-height: calc(100vh - 24px);
  padding: 20px;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(16, 185, 129, 0.08), transparent 24%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  color: #0f172a;
}

.workflow-builder-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.workflow-builder-eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.workflow-builder-title {
  margin: 6px 0 8px;
  font-size: 30px;
  line-height: 1.1;
}

.workflow-builder-subtitle {
  margin: 0;
  max-width: 760px;
  color: #475569;
}

.workflow-builder-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.workflow-builder-header-actions__status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.workflow-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  background: #e2e8f0;
  color: #334155;
}

.workflow-status--ok {
  background: rgba(16, 185, 129, 0.14);
  color: #0f766e;
}

.workflow-status--draft {
  background: rgba(148, 163, 184, 0.16);
  color: #334155;
}

.workflow-status--active {
  background: rgba(37, 99, 235, 0.16);
  color: #1d4ed8;
}

.workflow-status--archived {
  background: rgba(100, 116, 139, 0.16);
  color: #475569;
}

.workflow-status--warning {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}

.workflow-status--error {
  background: rgba(239, 68, 68, 0.15);
  color: #b91c1c;
}

.tool-button,
.primary-button,
.secondary-button {
  min-height: 38px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.tool-button {
  background: white;
  color: #0f172a;
  border-color: rgba(148, 163, 184, 0.32);
}

.tool-button:disabled,
.primary-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.primary-button {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: white;
}

.secondary-button {
  background: #f8fafc;
  color: #0f172a;
  border-color: rgba(148, 163, 184, 0.3);
}

.workflow-builder-body {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;
}

.workflow-sidebar {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.workflow-sidebar--left,
.workflow-sidebar--right {
  max-height: calc(100vh - 170px);
  overflow: auto;
  padding-right: 4px;
}

.sidebar-section {
  padding: 14px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.06);
}

.sidebar-section__title {
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.agent-card {
  padding: 14px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(14, 165, 233, 0.08));
}

.agent-card__label,
.agent-card__meta {
  font-size: 12px;
  color: #64748b;
}

.agent-card__value {
  margin: 6px 0;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.workflow-list,
.template-list,
.palette-grid,
.issues-list {
  display: grid;
  gap: 10px;
}

.template-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.template-tabs--overlay {
  margin-bottom: 0;
}

.template-tab {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.16s ease;
}

.template-tab:hover {
  border-color: rgba(37, 99, 235, 0.35);
  color: #1d4ed8;
}

.template-tab.is-active {
  background: rgba(37, 99, 235, 0.1);
  border-color: rgba(37, 99, 235, 0.3);
  color: #1d4ed8;
}

.workflow-empty-state {
  display: grid;
  gap: 8px;
  padding: 14px;
  border-radius: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.45);
  background: rgba(248, 250, 252, 0.8);
}

.workflow-empty-state strong {
  font-size: 13px;
  color: #0f172a;
}

.workflow-empty-state p {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
}

.workflow-empty-state__actions {
  display: flex;
  gap: 8px;
}

.workflow-list-item,
.template-card,
.palette-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 16px;
  background: white;
  text-align: left;
  cursor: pointer;
}

.template-card {
  align-items: center;
  gap: 12px;
  padding: 14px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.template-card:hover {
  transform: translateY(-1px);
  border-color: rgba(37, 99, 235, 0.3);
  box-shadow: 0 14px 24px rgba(37, 99, 235, 0.08);
}

.template-card--blank {
  border-style: dashed;
}

.workflow-list-item.is-active {
  border-color: rgba(37, 99, 235, 0.5);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.workflow-list-item__name {
  display: block;
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
}

.workflow-list-item__meta {
  margin-top: 4px;
  display: block;
  font-size: 12px;
  color: #64748b;
}

.template-card__icon,
.canvas-choice-card__icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(14, 165, 233, 0.12));
  color: #2563eb;
}

.template-card__icon :deep(svg),
.canvas-choice-card__icon :deep(svg) {
  width: 22px;
  height: 22px;
}

.template-card__body,
.canvas-choice-card__content {
  min-width: 0;
  display: grid;
  gap: 5px;
  flex: 1;
}

.template-card__body strong,
.canvas-choice-card__content strong {
  margin: 0;
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
}

.template-card__body span,
.canvas-choice-card__content span {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
}

.template-card__tags,
.canvas-choice-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.template-chip {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.08);
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  text-transform: none;
}

.template-card__cta {
  flex: none;
  align-self: center;
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
}

.palette-grid {
  grid-template-columns: 1fr;
}

.palette-card {
  align-items: center;
}

.palette-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: #e2e8f0;
  font-weight: 800;
  color: #334155;
}

.palette-card__text {
  min-width: 0;
}

.palette-card__text strong {
  display: block;
  font-size: 13px;
  color: #0f172a;
}

.palette-card__text small {
  display: block;
  margin-top: 2px;
  color: #64748b;
  line-height: 1.35;
}

.workflow-canvas-panel {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.workflow-canvas-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(255, 255, 255, 0.96);
}

.workflow-canvas-toolbar__group {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.field-inline,
.field-block {
  display: grid;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.field-inline input,
.field-block input,
.field-block textarea,
.field-block select {
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.34);
  background: white;
  color: #0f172a;
  font: inherit;
}

.field-inline {
  min-width: 240px;
}

.canvas-stage {
  position: relative;
  min-height: 72vh;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
  overflow: hidden;
}

.canvas-flow {
  width: 100%;
  height: 72vh;
}

.canvas-empty-state {
  position: absolute;
  inset: 18px;
  z-index: 5;
  display: grid;
  gap: 16px;
  align-content: start;
  padding: 20px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(6px);
  overflow: auto;
}

.canvas-empty-state__hero {
  display: grid;
  gap: 8px;
  max-width: 760px;
}

.canvas-empty-state__eyebrow {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.canvas-empty-state__hero h2 {
  margin: 0;
  font-size: 22px;
  line-height: 1.15;
  color: #0f172a;
}

.canvas-empty-state__hero p {
  margin: 0;
  max-width: 720px;
  color: #475569;
  line-height: 1.55;
}

.canvas-empty-state__choices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.canvas-choice-card {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  gap: 10px 12px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: white;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.05);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.canvas-choice-card:hover {
  transform: translateY(-2px);
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 16px 28px rgba(37, 99, 235, 0.12);
}

.canvas-choice-card__icon {
  grid-row: 1 / span 2;
  align-self: start;
}

.canvas-choice-card__cta {
  grid-column: 1 / -1;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 700;
  color: #2563eb;
}

.canvas-choice-card--blank {
  border-style: dashed;
  background: linear-gradient(180deg, rgba(37, 99, 235, 0.05), rgba(255, 255, 255, 1));
}

.canvas-hint {
  margin: 14px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.78);
  color: white;
  font-size: 12px;
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.2);
}

.workflow-status-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.workflow-status-strip__item {
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.workflow-status-strip__item strong {
  display: block;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.workflow-status-strip__item span {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}

.inspector-card {
  display: grid;
  gap: 12px;
}

.inspector-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.inspector-card__kind {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.inspector-card__title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.inspector-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.inspector-empty,
.issues-empty {
  padding: 14px;
  border-radius: 16px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
}

.issue-card {
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: #f8fafc;
  display: grid;
  gap: 4px;
}

.issue-card strong {
  font-size: 13px;
  color: #0f172a;
}

.issue-card span {
  font-size: 12px;
  color: #475569;
}

.issue-card--error {
  background: rgba(254, 226, 226, 0.9);
  border-color: rgba(239, 68, 68, 0.28);
}

.issue-card--warning {
  background: rgba(255, 247, 237, 0.92);
  border-color: rgba(245, 158, 11, 0.28);
}

.simulation-card {
  display: grid;
  gap: 12px;
}

.simulation-warning {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.28);
  color: #92400e;
  font-size: 13px;
  line-height: 1.45;
}

.simulation-output {
  display: grid;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(148, 163, 184, 0.22);
}

.simulation-output__row {
  display: grid;
  gap: 3px;
}

.simulation-output__row strong {
  font-size: 12px;
  color: #64748b;
}

.simulation-output__row span {
  font-size: 13px;
  color: #0f172a;
}

.workflow-builder-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
}

.status-text {
  font-size: 12px;
  color: #475569;
}

.status-text--error {
  color: #b91c1c;
}

@media (max-width: 1400px) {
  .workflow-builder-body {
    grid-template-columns: 260px minmax(0, 1fr);
  }

  .workflow-sidebar--right {
    grid-column: 1 / -1;
    max-height: none;
  }
}

@media (max-width: 1100px) {
  .workflow-builder-header {
    flex-direction: column;
  }

  .workflow-builder-body {
    grid-template-columns: 1fr;
  }

  .workflow-sidebar--left,
  .workflow-sidebar--right {
    max-height: none;
  }
}
</style>
